import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import cloneDeep from 'clone-deep';

import store from '@/store';
import router from '@/router';

import { ErrorAlertModule } from '@/store/modules/errors';
import { emptyToNull, errorHandler } from '@/utils/common';
import {
  createStatement,
  createStatementXml,
  getStatementById,
  getStatementXml,
  sendPackage,
  statementXmlBase64,
  notifyAdditionalSigners,
} from '@/api/services';
import {
  createEditedStatement,
  createEditedStatementXml,
  editedStatementXmlBase64,
  editedFormPackage,
  getEditedStatementInfo,
} from '@/api/editedStatement';
import { RouteName } from '@/router';
import { IStatement, StatementType, MapStatementData } from '@/types/services';
import statementTypeToMapStatement from './statementTypeToMapStatement';

export interface IServicesState {
  statement: IStatement;
  runSign: Function;
  dataUserCertificate: DataUserCertificateI;
}

interface DataUserCertificateI {
  userLastName: string;
  userFirstName: string;
  userMiddleName: string;
  snils: string;
  inn: string;
}

interface IVariants {
  [index: string]: IStatement;
}

interface MapStatementPayload {
  data: MapStatementData;
  statementType: StatementType;
}

const statementVariants: IVariants = {
  [StatementType.NOTICE_RECOVERY]: {
    statementType: StatementType.NOTICE_RECOVERY,
    subjects: {
      applicant: {},
    },
    documents: [],
    prevCreditorDocuments: [],
    prevDebtorDocuments: [],
    statementParamDocuments: [],
    notarialDocuments: [],
    stepsCompleted: [],
  },
  [StatementType.RUZDI]: {
    statementType: StatementType.RUZDI,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.SECURING_EVIDENCE]: {
    statementType: StatementType.SECURING_EVIDENCE,
    subjects: {
      applicant: {},
    },
    securityEvidenceDetails: {},
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.ACCEPTANCE_DEPOSIT]: {
    statementType: StatementType.ACCEPTANCE_DEPOSIT,
    subjects: {
      debtor: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.TRANSFER_EL_DOCS]: {
    statementType: StatementType.TRANSFER_EL_DOCS,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
    transferDocuments: [],
  },
  [StatementType.CORRECT_TRANSLATION]: {
    statementType: StatementType.CORRECT_TRANSLATION,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.ACCEPTANCE_ESCROW]: {
    statementType: StatementType.ACCEPTANCE_ESCROW,
    subjects: {
      debtors: [],
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.STORAGE_ACCEPT]: {
    statementType: StatementType.STORAGE_ACCEPT,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.STORAGE_ISSUE]: {
    statementType: StatementType.STORAGE_ISSUE,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.DOCUMENT_CONVERSION]: {
    statementType: StatementType.DOCUMENT_CONVERSION,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.ROD]: {
    statementType: StatementType.ROD,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.EXTRACTION_ROD]: {
    statementType: StatementType.EXTRACTION_ROD,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
  [StatementType.EGRN_CONFIRM]: {
    statementType: StatementType.EGRN_CONFIRM,
    subjects: {
      applicant: {},
    },
    documents: [],
    stepsCompleted: [],
  },
};

const SubjectName = {
  APPLICANT: 'applicant',
  DEBTOR: 'debtor',
};

const statementTypeToCertificateSubjectName = {
  [StatementType.NOTICE_RECOVERY]: SubjectName.APPLICANT,
  [StatementType.RUZDI]: SubjectName.APPLICANT,
  [StatementType.SECURING_EVIDENCE]: SubjectName.APPLICANT,
  [StatementType.ACCEPTANCE_DEPOSIT]: SubjectName.DEBTOR,
  [StatementType.TRANSFER_EL_DOCS]: SubjectName.APPLICANT,
  [StatementType.STORAGE_ACCEPT]: SubjectName.APPLICANT,
  [StatementType.STORAGE_ISSUE]: SubjectName.APPLICANT,
  [StatementType.CORRECT_TRANSLATION]: SubjectName.APPLICANT,
  [StatementType.DOCUMENT_CONVERSION]: SubjectName.APPLICANT,
  [StatementType.ROD]: SubjectName.APPLICANT,
  [StatementType.EXTRACTION_ROD]: SubjectName.APPLICANT,
  [StatementType.EGRN_CONFIRM]: SubjectName.APPLICANT,
};

@Module({ dynamic: true, store, name: 'services' })
class Services extends VuexModule implements IServicesState {
  statement: any = {};
  runSign!: (t: any) => Promise<any>;
  dataUserCertificate: any = {};

  // создание модели заявления
  @Mutation
  private CREATE_MODEL(name: string) {
    this.statement = cloneDeep(statementVariants[name]);
  }

  @Action
  createModel(name: string) {
    this.CREATE_MODEL(name);
    return this.statement;
  }

  // создание модели заявления

  @Mutation
  private SET_STATEMENT(data: object) {
    this.statement = { ...data };
  }

  @Mutation
  private SET_STATEMENT_ID(id: string) {
    this.statement.id = id;
  }

  @Mutation
  private SET_STATEMENT_XML(fileLink: string) {
    this.statement.xmlFileLink = fileLink;
  }

  @Mutation
  private SET_STATEMENT_XML_SIG_BASE64(data: string) {
    this.statement.xmlSigBase64 = data;
  }

  @Mutation
  private SET_DISTRICT_REGION(data: any) {
    this.statement.regionCode = data.regionCode;
    this.statement.districtId = data.districtId;
    this.statement.chamberId = data.chamberId;
  }

  /*
                Памятка, маппинг проверок сертификата с подписантом:
                ФЛ - фио, снилс, инн
                ФЛ -  (представитель) - фио, снилс, инн —- данные представителя!
                ИП - фио, снилс, инн, огрнип
                ИП - (представитель) - фио, снилс, инн —- данные представителя!
                ЮЛ - (представитель) - фио, снилс, (орнанизация) - инн, огрн
              */
  @Mutation
  private SET_DATA_USER_CERTIFICATE() {
    const subjectName =
      // @ts-ignore
      statementTypeToCertificateSubjectName[this.statement.statementType];

    if (!subjectName) return;
    const subject = this.statement.subjects[subjectName];
    this.dataUserCertificate = subject;
  }

  @Mutation
  MAP_STATEMENT({ data, statementType }: MapStatementPayload) {
    statementTypeToMapStatement[statementType](this.statement, data);
  }

  @Mutation
  SET_STEPS_COMPLETED(data: string) {
    this.statement.stepsCompleted.push(data);
  }

  @Mutation
  SET_STEPS_COMPLETED_CLEAR() {
    this.statement.stepsCompleted = [];
  }

  @Action({ rawError: true })
  addStepsCompleted(data: string) {
    if (!this.statement.stepsCompleted) this.statement.stepsCompleted = [];
    if (!this.statement.stepsCompleted?.includes(data)) {
      this.SET_STEPS_COMPLETED(data);
    }
  }

  @Action({ rawError: true })
  clearStepsCompleted() {
    this.SET_STEPS_COMPLETED_CLEAR();
  }

  @Action
  editStatement(data: object) {
    this.SET_STATEMENT(data);
  }

  @Action
  setDistrictRegion(data: object) {
    this.SET_DISTRICT_REGION(data);
  }

  @Action
  setStatementXmlSigBase64(data: string) {
    this.SET_STATEMENT_XML_SIG_BASE64(data);
  }

  @Action({ rawError: true })
  async saveStatement(isAlternativeServiceEdit: boolean) {
    const createStatementService = isAlternativeServiceEdit
      ? createEditedStatement
      : createStatement;
    ErrorAlertModule.setErrorAlert('');
    ErrorAlertModule.setErrorAlertXml('');
    try {
      const data: any = await createStatementService(
        emptyToNull(this.statement) as IStatement
      );

      if (!isAlternativeServiceEdit) {
        this.SET_STATEMENT_ID(data.id);
        await this.setRouteStatementId();
      }

      return true;
    } catch (error) {
      errorHandler(error);
    }
  }

  @Action
  async getStatementXml(statementId: string) {
    return await getStatementXml(statementId);
  }

  @Action
  public async setRouteStatementId() {
    if (!this.statement.id) return;

    const routeStatementId =
      router.currentRoute.params && router.currentRoute.params.statementId;

    if (routeStatementId === this.statement.id) return;

    await router.replace({
      // @ts-ignore
      name: RouteName[this.statement.statementType],
      params: { statementId: this.statement.id },
    });
  }

  @Action({ rawError: true })
  async getStatementById({
    id,
    isAlternativeServiceEdit,
  }: {
    id: string;
    isAlternativeServiceEdit: boolean;
  }) {
    ErrorAlertModule.setErrorAlert('');

    try {
      const getStatementService = isAlternativeServiceEdit
        ? getEditedStatementInfo
        : getStatementById;
      const data = await getStatementService(id);

      if (!data) {
        // Открытие заявления другого пользователя: возвращает 200 и пустоту
        ErrorAlertModule.setErrorAlert('Заявление не найдено');
        return false;
      } else {
        this.SET_STATEMENT(data);
        return true;
      }
    } catch (error) {
      errorHandler(error);
      return false;
    }
  }

  @Action({ rawError: true })
  async createStatementXml(isAlternativeServiceEdit?: boolean) {
    const createStatementXmlService = isAlternativeServiceEdit
      ? createEditedStatementXml
      : createStatementXml;
    ErrorAlertModule.setErrorAlert('');
    ErrorAlertModule.setErrorAlertXml('');

    try {
      const id = isAlternativeServiceEdit
        ? this.statement.originalStatementId
        : this.statement.id;
      const data: any = await createStatementXmlService(id);
      this.SET_STATEMENT_XML(data.fileLink);
      // данные заявителя для сверки с сертификатом
      this.SET_DATA_USER_CERTIFICATE();

      return true;
    } catch (error) {
      errorHandler(error, true);

      return false;
    }
  }

  @Action({ rawError: true })
  async statementXmlBase64(isAlternativeServiceEdit: boolean) {
    const statementXmlBase64Service = isAlternativeServiceEdit
      ? editedStatementXmlBase64
      : statementXmlBase64;

    ErrorAlertModule.setErrorAlert('');

    try {
      const id = isAlternativeServiceEdit
        ? this.statement.originalStatementId
        : this.statement.id;

      return await statementXmlBase64Service(id);
    } catch (error) {
      errorHandler(error);

      throw error;
    }
  }

  @Action({ rawError: true })
  async sendPackage() {
    ErrorAlertModule.setErrorAlert('');

    try {
      await sendPackage({
        statementId: this.statement.id,
        regionCode: this.statement.regionCode,
        districtId: this.statement.districtId,
        chamberId: this.statement.chamberId,
        xmlSigBase64: this.statement.xmlSigBase64,
      });
    } catch (error) {
      errorHandler(error);

      throw error;
    }
  }

  @Action({ rawError: true })
  async editedFormPackage() {
    ErrorAlertModule.setErrorAlert('');

    try {
      await editedFormPackage({
        statementId: this.statement.originalStatementId,
        xmlSigBase64: this.statement.xmlSigBase64,
      });
    } catch (error) {
      errorHandler(error);
    }
  }

  @Action({ rawError: true })
  async notifyAdditionalSigners() {
    ErrorAlertModule.setErrorAlert('');
    try {
      await notifyAdditionalSigners({
        statementId: this.statement.id,
        regionCode: this.statement.regionCode,
        districtId: this.statement.districtId,
        chamberId: this.statement.chamberId,
      });
    } catch (error) {
      errorHandler(error);

      throw error;
    }
  }

  @Action
  setFunctionRunSign(runSign: (t: any) => Promise<any>) {
    this.runSign = runSign;
  }
}

export const ServicesModule = getModule(Services);
